import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowLeft, Search, Plus } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSales } from '../../../contexts/SalesContext';
import { useFinancial, openCashRegister } from '../../../contexts/FinancialContext';
import { SegmentedControl } from '../../../components/ui/SegmentedControl';
import PaymentModal from '../../sales/detail/components/PaymentModal';
import { SuccessScreen } from '../../../components/feedback/SuccessScreen';
import { CajaResumen } from './CajaResumen';
import { MovimientoBottomSheet } from './MovimientoBottomSheet';
import { MovimientoForm } from './MovimientoForm';
import { CajaOpeningModal } from './CajaOpeningModal';
import { CajaClosingModal } from './CajaClosingModal';
import { toast } from 'sonner';

export function CajaScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const listRef = useRef(null);
  const saleRefs = useRef({});
  const { sales } = useSales();
  const { 
    currentClosing, 
    balances, 
    loading, 
    openCashRegister, 
    closeRegister,
    transactions,
    getTransactions 
  } = useFinancial();
  const [searchTerm, setSearchTerm] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [activeTab, setActiveTab] = useState('movimientos'); // 'movimientos', 'pendientes', 'cobradas'
  const [showMovimientoSheet, setShowMovimientoSheet] = useState(false);
  const [showMovimientoForm, setShowMovimientoForm] = useState(false);
  const [tipoMovimiento, setTipoMovimiento] = useState(null);
  const [showOpeningModal, setShowOpeningModal] = useState(false);
  const [showClosingModal, setShowClosingModal] = useState(false);
  const [successVariant, setSuccessVariant] = useState('payment');
  const [localTransactions, setLocalTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);

  // Restaurar la posición del scroll cuando volvemos
  useEffect(() => {
    if (location.state?.saleId && saleRefs.current[location.state.saleId]) {
      saleRefs.current[location.state.saleId].scrollIntoView({ 
        behavior: 'auto',
        block: 'center'
      });
    }
  }, [location.state]);

  // Restaurar el tab activo cuando volvemos
  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    }
  }, [location.state]);

  // Usar datos reales del FinancialContext
  const cajaData = {
    efectivo: balances?.cash || 0,
    transferencias: balances?.transfer || 0,
    credito: balances?.credit || 0,
    otros: balances?.other || 0,
    pending: balances?.pending || { amount: 0, count: 0 }
  };

  // Filtrar ventas según el estado de pago
  const filteredSalesByStatus = sales?.filter(sale => {
    if (activeTab === 'pendientes') {
      return sale.payment_status === 'unpaid' || sale.payment_status === 'partial';
    } else if (activeTab === 'cobradas') {
      return sale.payment_status === 'paid';
    }
    return true;
  }) || [];

  // Filtrar por búsqueda
  const filteredSales = filteredSalesByStatus.filter(sale => {
    const searchLower = searchTerm.toLowerCase();
    return (
      sale.display_id?.toString().includes(searchLower) ||
      sale.client?.name?.toLowerCase().includes(searchLower) ||
      sale.items?.some(item => 
        item.title?.toLowerCase().includes(searchLower) ||
        item.lot?.title?.toLowerCase().includes(searchLower)
      )
    );
  });

  // Cargar transacciones (usando useCallback para memoizar la función)
  const loadTransactions = useCallback(async () => {
    try {
      setLoadingTransactions(true);
      const data = await getTransactions();
      setLocalTransactions(data);
    } catch (error) {
      console.error('Error cargando transacciones:', error);
    } finally {
      setLoadingTransactions(false);
    }
  }, [getTransactions]);
  
  // Cargar transacciones al iniciar el componente
  useEffect(() => {
    if (getTransactions) {
      loadTransactions();
    }
  }, [getTransactions, loadTransactions]);

  // Recargar transacciones cuando se registra un nuevo movimiento o se cambia a la pestaña de movimientos
  useEffect(() => {
    if (activeTab === 'movimientos' && getTransactions) {
      loadTransactions();
    }
  }, [activeTab, showSuccess, getTransactions, loadTransactions]);

  const handlePaymentClick = (sale) => {
    setSelectedSale(sale);
    setShowPaymentModal(true);
  };

  const handlePaymentConfirm = async (paymentInfo) => {
    try {
      // Aquí iría la lógica para actualizar el pago en la base de datos
      console.log('Payment confirmed:', paymentInfo);
      setShowPaymentModal(false);
      setSelectedSale(null);
      setShowSuccess(true);
    } catch (error) {
      console.error('Error al registrar el pago:', error);
    }
  };

  const handleMovimientoSelect = (tipo) => {
    setTipoMovimiento(tipo);
    setShowMovimientoSheet(false);
    setShowMovimientoForm(true);
  };

  const handleMovimientoConfirm = async (movimiento) => {
    try {
      console.log('Movimiento registrado:', movimiento);
      
      // Si la transacción ya fue registrada por el componente MovimientoForm
      if (movimiento.transaction) {
        setShowMovimientoForm(false);
        setSuccessVariant('payment');
        setShowSuccess(true);
        
        // Mostrar notificación toast
        toast.success(`${movimiento.tipo === 'ingreso' ? 'Ingreso' : 'Egreso'} registrado con éxito`);
      }
    } catch (error) {
      console.error('Error al registrar movimiento:', error);
      toast.error('Error al registrar el movimiento');
    }
  };

  const handleSaleClick = (sale) => {
    if (activeTab === 'pendientes') {
      handlePaymentClick(sale);
    } else if (activeTab === 'cobradas') {
      navigate(`/sales/${sale.id}`, { 
        state: { 
          returnTo: '/caja',
          saleId: sale.id,
          activeTab
        }
      });
    }
  };

  const handleOpenCaja = async (data) => {
    try {
      console.log("Opening cash register with data:", data);
      await openCashRegister({
        openingBalance: data.balance,
        notes: data.notes
      });
      
      setShowOpeningModal(false);
      toast.success('Caja abierta exitosamente');
      
      // No mostrar la pantalla de éxito, la UI se actualizará automáticamente 
      // setSuccessVariant('payment');
      // setShowSuccess(true);
    } catch (error) {
      console.error("Error opening cash register:", error);
      toast.error('Error al abrir la caja');
    }
  };

  const handleCloseCaja = async (data) => {
    try {
      console.log("CajaScreen - Cerrando caja con datos:", data);
      
      // Verificar que tenemos todos los datos necesarios
      if (!data.balances) {
        console.error("CajaScreen - Error: Faltan datos de saldos para cerrar la caja");
        toast.error('Error al cerrar la caja: Datos incompletos');
        return;
      }
      
      const result = await closeRegister({
        balance: data.balances,
        discrepancy: data.discrepancies || {},
        notes: data.notes
      });
      
      console.log("CajaScreen - Caja cerrada exitosamente:", result);
      setShowClosingModal(false);
      toast.success('Caja cerrada exitosamente');
      
    } catch (error) {
      console.error("CajaScreen - Error al cerrar la caja:", error);
      toast.error(`Error al cerrar la caja: ${error.message || 'Error desconocido'}`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 relative">
      {/* Header */}
      <div className="sticky top-0 bg-white dark:bg-gray-800 border-b dark:border-gray-700 z-10">
        <div className="p-4">
          {/* Back button and title */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/')}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors text-gray-900 dark:text-white"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Caja
              </h1>
            </div>
            
            {loading ? (
              <div className="animate-pulse h-8 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
            ) : currentClosing ? (
              <div className="flex items-center gap-3">
                <div className="px-3 py-1 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-sm font-medium">
                  Caja Abierta
                </div>
                <button 
                  onClick={() => setShowClosingModal(true)}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-white rounded-lg font-medium"
                >
                  Cerrar Caja
                </button>
              </div>
            ) : (
              <button 
                onClick={() => setShowOpeningModal(true)}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium"
              >
                Abrir Caja
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="p-4">
        {/* Resumen de Caja */}
        <div className="mb-4">
          <CajaResumen 
            data={cajaData} 
            isOpen={!!currentClosing}
          />
        </div>

        {/* Tabs */}
        <SegmentedControl
          value={activeTab}
          onChange={setActiveTab}
          options={[
            { value: 'movimientos', label: 'Movimientos' },
            { value: 'pendientes', label: 'Pendientes' },
            { value: 'cobradas', label: 'Cobradas' }
          ]}
          disabled={!currentClosing && activeTab === 'movimientos'}
        />

        {/* Search */}
        <div className="relative mt-4">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar por número, cliente o producto..."
            className="w-full bg-white dark:bg-gray-800 pl-10 pr-4 py-2 rounded-lg border dark:border-gray-700"
            disabled={!currentClosing && activeTab === 'movimientos'}
          />
        </div>
      </div>

      {/* List */}
      <div ref={listRef} className="p-4 space-y-4 overflow-auto pb-24" style={{ height: 'calc(100vh - 250px)' }}>
        {activeTab === 'movimientos' ? (
          loadingTransactions ? (
            <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-sm border dark:border-gray-700 text-center">
              <p className="text-gray-500 dark:text-gray-400">
                Cargando movimientos...
              </p>
            </div>
          ) : localTransactions && localTransactions.length > 0 ? (
            localTransactions.map(transaction => (
              <div key={transaction.id} className="w-full bg-white dark:bg-gray-800 rounded-lg p-3 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-base font-bold text-gray-900 dark:text-white truncate">
                        {transaction.type === 'income' ? 'Ingreso' : 
                         transaction.type === 'expense' ? 'Egreso' : 
                         transaction.type === 'sale_payment' ? 'Pago de venta' : 
                         'Movimiento'}
                      </span>
                      <span className={`shrink-0 px-1.5 py-0.5 ${
                        transaction.type === 'income' ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' : 
                        transaction.type === 'expense' ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400' : 
                        'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                      } text-xs font-medium rounded`}>
                        {transaction.payment_method === 'cash' ? 'Efectivo' : 
                         transaction.payment_method === 'transfer' ? 'Transferencia' : 
                         transaction.payment_method}
                      </span>
                    </div>
                    
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {transaction.description}
                    </div>
                    
                    <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                      {new Date(transaction.created_at).toLocaleString('es-AR', {
                        day: 'numeric',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  </div>
                  
                  <div className="text-right shrink-0">
                    <div className={`text-lg font-bold ${
                      transaction.type === 'income' ? 'text-green-600 dark:text-green-400' : 
                      transaction.type === 'expense' ? 'text-red-600 dark:text-red-400' : 
                      'text-gray-900 dark:text-white'
                    }`}>
                      {transaction.type === 'income' ? '+' : transaction.type === 'expense' ? '-' : ''}
                      ${transaction.amount.toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-sm border dark:border-gray-700 text-center">
              <p className="text-gray-500 dark:text-gray-400">
                No hay movimientos registrados
              </p>
            </div>
          )
        ) : filteredSales.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-sm border dark:border-gray-700 text-center">
            <p className="text-gray-500 dark:text-gray-400">
              {searchTerm 
                ? 'No se encontraron ventas que coincidan con la búsqueda'
                : activeTab === 'pendientes' 
                  ? 'No hay ventas pendientes de cobro'
                  : 'No hay ventas cobradas'}
            </p>
          </div>
        ) : (
          filteredSales.map(sale => (
            <button
              key={sale.id}
              ref={el => saleRefs.current[sale.id] = el}
              onClick={() => handleSaleClick(sale)}
              className={`w-full bg-white dark:bg-gray-800 rounded-lg p-3 shadow-sm ${
                activeTab === 'pendientes' ? 'hover:shadow-md transition-shadow' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                {/* Información principal */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-base font-bold text-gray-900 dark:text-white truncate">
                      Venta #{sale.display_id}
                    </span>
                    {sale.payment_status === 'partial' && (
                      <span className="shrink-0 px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 text-xs font-medium rounded">
                        Pago parcial
                      </span>
                    )}
                    {sale.payment_status === 'unpaid' && (
                      <span className="shrink-0 px-1.5 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-xs font-medium rounded">
                        Falta cobrar
                      </span>
                    )}
                    {sale.payment_status === 'paid' && (
                      <span className="shrink-0 px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 text-xs font-medium rounded">
                        Cobrada
                      </span>
                    )}
                  </div>
                  
                  {/* Info secundaria en una línea */}
                  <div className="flex items-center gap-2 text-sm">
                    {sale.client && (
                      <>
                        <span className="text-gray-900 dark:text-gray-100 font-medium truncate">
                          {sale.client.name}
                        </span>
                        <span className="text-gray-400">·</span>
                      </>
                    )}
                    <span className="text-gray-500 dark:text-gray-400 truncate">
                      {new Date(sale.created_at).toLocaleString('es-AR', {
                        day: 'numeric',
                        month: 'long',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                </div>

                {/* Montos */}
                <div className="text-right shrink-0">
                  <div className="text-lg font-bold text-gray-900 dark:text-white">
                    ${(sale.total_amount || 0).toLocaleString()}
                  </div>
                  {sale.payment_status === 'partial' && (
                    <div className="text-xs font-medium text-red-600 dark:text-red-400">
                      Pendiente: ${((sale.total_amount || 0) - (sale.paid_amount || 0)).toLocaleString()}
                    </div>
                  )}
                </div>
              </div>
            </button>
          ))
        )}
      </div>

      {/* Link a Cajas Anteriores */}
      <div className="px-4 py-3 mt-2 mb-16 text-center">
        <button
          onClick={() => navigate('/caja/historial')}
          className="text-blue-600 dark:text-blue-400 text-sm font-medium hover:underline"
        >
          Ver cajas anteriores
        </button>
      </div>

      {/* Floating Action Button - Solo visible en la solapa de movimientos y cuando la caja está abierta */}
      {activeTab === 'movimientos' && currentClosing && (
        <button
          onClick={() => setShowMovimientoSheet(true)}
          className="fixed right-6 bottom-6 w-14 h-14 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center z-20"
          aria-label="Nuevo movimiento"
        >
          <Plus className="w-6 h-6" />
          <span className="sr-only">Nuevo movimiento</span>
        </button>
      )}

      {/* Modals */}
      {selectedSale && (
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => {
            setShowPaymentModal(false);
            setSelectedSale(null);
          }}
          onConfirm={handlePaymentConfirm}
          totalAmount={selectedSale.total_amount - (selectedSale.paid_amount || 0)}
          saleId={selectedSale.id}
        />
      )}

      {/* Movimiento Bottom Sheet */}
      <MovimientoBottomSheet
        isOpen={showMovimientoSheet}
        onClose={() => setShowMovimientoSheet(false)}
        onSelectMovimiento={handleMovimientoSelect}
      />

      {/* Movimiento Form */}
      <MovimientoForm
        isOpen={showMovimientoForm}
        onClose={() => setShowMovimientoForm(false)}
        tipo={tipoMovimiento}
        onConfirm={handleMovimientoConfirm}
      />

      {/* Success Screen */}
      <SuccessScreen 
        show={showSuccess}
        variant={successVariant}
        onClose={() => setShowSuccess(false)}
      />

      {/* Modals */}
      <CajaOpeningModal
        isOpen={showOpeningModal}
        onClose={() => setShowOpeningModal(false)}
        onConfirm={handleOpenCaja}
      />
      
      <CajaClosingModal
        isOpen={showClosingModal}
        onClose={() => setShowClosingModal(false)}
        onConfirm={handleCloseCaja}
        currentBalances={balances}
      />
    </div>
  );
} 